import React from 'react';
import { Icon, Tag } from '@tt/ui';
import PropTypes from 'prop-types';

import RentPaymentTypeEnum from '../../../constants/enums/RentPaymentTypeEnum';
import getCardBrand from '../../../helpers/getCardBrand';
import { isCardExpired } from '../../../helpers/isCardExpired';
import isCardExpiringSoon from '../../../helpers/isCardExpiringSoon';
import { EnumPaymentMethodStatusTypes } from '../../../pages/enums';
import PaymentCardIcon from '../../../pages/renters/payments/PaymentCardIcon';
import paymentMethodPropTypes from '../../../prop-types/payment-method-prop-types';
import { Paragraph } from '../../Typography';

import DeleteButton from './DeleteButton';
import EditButton from './EditButton';
import LogInBankAlert from './LogInBankAlert';
import VerifyBankAlert from './VerifyBankAlert';

import styles from './PaymentMethodInfo.module.scss';

const PaymentMethodInfo = ({
  paymentMethod,
  removable,
  editable,
  hideLabels,
  hideDescription,
  showPlaidActions,
  onEdit,
  onRemove,
}) => {
  if (paymentMethod.type === RentPaymentTypeEnum.BANK_ACCOUNT) {
    return (
      <div className={styles.bankContainer}>
        <div className={styles.bankBase}>
          <Icon name="bank-account" size="xxl" />
          <div className={styles.content}>
            <div className={styles.cardDetails}>
              <Paragraph weight="semi">
                Bank Account ending in {paymentMethod.last4}
              </Paragraph>
            </div>
            <div className={styles.tags}>
              {paymentMethod.preferred_for_auto_pay && (
                <Tag color="lightGrey">Autopay</Tag>
              )}
            </div>
          </div>
          {removable && (
            <DeleteButton paymentMethod={paymentMethod} onRemove={onRemove} />
          )}
          {editable && <EditButton onClick={onEdit} />}
        </div>
        {showPlaidActions &&
          paymentMethod.source_status === EnumPaymentMethodStatusTypes.NEW && (
            <VerifyBankAlert paymentMethodId={paymentMethod.id} />
          )}
        {showPlaidActions &&
          paymentMethod.source_status ===
            EnumPaymentMethodStatusTypes.LOGIN_EXPIRED && (
            <LogInBankAlert paymentMethodId={paymentMethod.id} />
          )}
      </div>
    );
  }
  return (
    <div className={styles.container}>
      <PaymentCardIcon brand={paymentMethod.brand} className={styles.icon} />
      <div className={styles.content}>
        <div className={styles.cardDetails}>
          <Paragraph weight="semi">
            {`${getCardBrand(
              paymentMethod.brand,
            )} ending in ${paymentMethod.last4}`}
          </Paragraph>
          {!hideDescription && (
            <Paragraph className={styles.convenienceFee}>
              Expires {paymentMethod.expiration}
            </Paragraph>
          )}
        </div>
        {!hideLabels && (
          <div className={styles.tags}>
            {isCardExpiringSoon(paymentMethod) && (
              <Tag color="yellow">Expiring Soon</Tag>
            )}
            {isCardExpired(paymentMethod.expiration) && (
              <Tag color="red">Expired</Tag>
            )}
            {paymentMethod.preferred_for_auto_pay && (
              <Tag color="lightGrey">Autopay</Tag>
            )}
            {paymentMethod.preferred_for_rent_reporting && (
              <Tag color="lightGrey">Rent Reporting</Tag>
            )}
          </div>
        )}
      </div>
      {removable && (
        <DeleteButton paymentMethod={paymentMethod} onRemove={onRemove} />
      )}
      {editable && <EditButton onClick={onEdit} />}
    </div>
  );
};

PaymentMethodInfo.propTypes = {
  removable: PropTypes.bool,
  editable: PropTypes.bool,
  hideLabels: PropTypes.bool,
  hideDescription: PropTypes.bool,
  paymentMethod: paymentMethodPropTypes.isRequired,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
  showPlaidActions: PropTypes.bool,
};

export default PaymentMethodInfo;
